import type { ReactNode } from 'react'
import { CircleCheckBig, Info, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Switch } from '@/components/ui/Switch'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/lib/utils'

/**
 * The parts every settings page is built from (order confirmation and sales order, workflow,
 * supplier and customer). Layout of a page: the automation card on top, then the flagging
 * section beside a column of thresholds and single switches, then Advanced in two columns.
 */

/** Two columns from lg: the wide one for the flagging section, the narrow one for the rest. */
export function SettingsMainGrid({ wide, narrow }: { wide: ReactNode; narrow: ReactNode }) {
  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="min-w-0">{wide}</div>
      <div className="min-w-0 space-y-6">{narrow}</div>
    </div>
  )
}

export function AdvancedSettings({
  children,
  description = 'How documents are read. Rarely changed after setup.',
}: {
  children: ReactNode
  description?: string
}) {
  return (
    <section className="space-y-4 border-t border-sheet-border pt-6">
      <div>
        <h2 className="text-sm font-semibold text-gray-900">Advanced</h2>
        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
      </div>
      {/* Same split as the main grid above, so the columns line up down the page. */}
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">{children}</div>
    </section>
  )
}

function AboutTip({ title, about }: { title: string; about: string }) {
  return (
    <Tooltip content={about}>
      <button type="button" className="inline-flex text-gray-400 hover:text-gray-600" aria-label={`About ${title.toLowerCase()}`}>
        <Info className="h-4 w-4" />
      </button>
    </Tooltip>
  )
}

/** A white card with a titled header; `actions` sit at the header's right (edit, turn all on). */
export function SettingsCard({
  title,
  about,
  description,
  actions,
  children,
  className,
}: {
  title: string
  about?: string
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('rounded-sheet border border-sheet-border bg-white', className)}>
      <header className="flex min-h-12 items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            {about && <AboutTip title={title} about={about} />}
          </div>
          {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  )
}

/** The pencil in a card header that opens its values for editing. */
export function EditButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-gray-900" aria-label={label} onClick={onClick}>
      <Pencil className="h-4 w-4" />
    </Button>
  )
}

/** "Turn all on" / "Turn all off" for a group of switches. */
export function ToggleAllButton({ allOn, onToggle, what }: { allOn: boolean; onToggle: (on: boolean) => void; what?: string }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(!allOn)}
      className="rounded-full px-2.5 py-1 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50 hover:text-brand-800"
    >
      {allOn ? 'Turn all off' : 'Turn all on'}
      {what && <span className="sr-only"> {what}</span>}
    </button>
  )
}

/**
 * One setting with a switch. `spread`: text on the left, the switch at the right edge (single
 * settings). `compact`: the switch first, then the text (long lists of fields, in columns).
 */
export function SettingToggle({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  layout = 'spread',
}: {
  label: ReactNode
  description?: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  layout?: 'spread' | 'compact'
}) {
  const text = (
    <span className="min-w-0">
      <span className={cn('block text-sm', disabled ? 'text-gray-400' : 'text-gray-800')}>{label}</span>
      {description && <span className="mt-0.5 block text-xs text-gray-500">{description}</span>}
    </span>
  )
  return (
    <label
      className={cn(
        'flex gap-3',
        layout === 'spread' ? 'items-start justify-between py-2' : 'items-center py-1.5',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      )}
    >
      {layout === 'spread' ? (
        <>
          {text}
          <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} className="mt-px" />
        </>
      ) : (
        <>
          <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} size="sm" />
          {text}
        </>
      )}
    </label>
  )
}

/** A group heading inside a card, e.g. "Delivery address", with an optional action. */
export function SettingGroup({ title, action, children, className }: { title?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      {(title || action) && (
        <div className="mb-1 flex items-center justify-between gap-2">
          {title && <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  )
}

/**
 * The fully automatic validation switch, set apart from the rest: it decides whether anyone
 * looks at a document at all. Says in words what the current choice means.
 */
export function AutomationCard({
  checked,
  onChange,
  title = 'Fully automatic validation',
  description,
  whenOn,
  whenOff,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  title?: string
  description: string
  whenOn: string
  whenOff: string
}) {
  return (
    <section className="relative overflow-hidden rounded-sheet border border-sheet-border bg-white shadow-panel">
      {/* Fades in when on; gradients can't transition, so this layer's opacity does. */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 bg-gradient-to-r from-brand-50 via-brand-50/60 to-white transition-opacity duration-500 motion-reduce:transition-none',
          checked ? 'opacity-100' : 'opacity-0',
        )}
      />
      <label className="relative flex cursor-pointer items-start gap-4 px-5 py-5 sm:items-center sm:gap-5 sm:px-6">
        {/* The badge needs room; on a phone the text gets the width instead. */}
        <span
          className={cn(
            'hidden h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors duration-300 sm:flex',
            checked ? 'bg-gradient-to-br from-brand-600 to-brand-900 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]' : 'bg-gray-100 text-gray-400',
          )}
          aria-hidden
        >
          <CircleCheckBig className="h-6 w-6" strokeWidth={1.75} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-display text-lg font-bold leading-tight tracking-tight text-gray-900">{title}</span>
          <span className="mt-1 block text-sm text-gray-600">{description}</span>
          <span className={cn('mt-2 flex items-start gap-1.5 text-xs font-medium', checked ? 'text-brand-700' : 'text-gray-500')}>
            <span className={cn('mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full', checked ? 'bg-emerald-500' : 'bg-gray-400')} aria-hidden />
            {checked ? whenOn : whenOff}
          </span>
        </span>
        <Switch checked={checked} onCheckedChange={onChange} size="lg" />
      </label>
    </section>
  )
}

/** Threshold values as tiles: the number big, its unit and what it means small. */
export function ValueTiles({ items }: { items: { label: string; value: ReactNode; unit?: string }[] }) {
  return (
    <dl className={cn('grid gap-3', items.length === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
      {items.map((item) => (
        <div key={item.label} className="rounded-chip bg-gray-50 px-3 py-2.5">
          <dt className="text-xs text-gray-500">{item.label}</dt>
          <dd className="mt-1 font-display text-2xl font-bold leading-none tracking-tight tabular-nums text-gray-900">
            {item.value}
            {item.unit && <span className="ml-1 font-sans text-xs font-medium tracking-normal text-gray-500">{item.unit}</span>}
          </dd>
        </div>
      ))}
    </dl>
  )
}
